/**
 * @file ManageLeaveTypesModal.tsx
 * @brief Modal to view and manage leave types from enum_master.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import { Plus, Trash2, Umbrella, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  enumMasterService,
  LEAVE_TYPE_KEY,
  LEAVE_TYPE_MODULE,
  type EnumMasterItem,
} from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

export interface ManageLeaveTypesModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when a new leave type is created so the parent form can select it. */
  onLeaveTypeAdded?: (keyValue: string) => void;
}

/**
 * ManageLeaveTypesModal displays current leave types and lets managers add or deactivate them.
 */
const ManageLeaveTypesModal: React.FC<ManageLeaveTypesModalProps> = ({
  open,
  onClose,
  onLeaveTypeAdded,
}) => {
  const queryClient = useQueryClient();
  const [newLabel, setNewLabel] = useState('');
  const [confirmItem, setConfirmItem] = useState<EnumMasterItem | null>(null);

  const { data: leaveTypes = [], isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.leaveTypes(true),
    queryFn: () => enumMasterService.listLeaveTypes(true),
    enabled: open,
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (displayLabel: string) =>
      enumMasterService.create({
        moduleName: LEAVE_TYPE_MODULE,
        keyName: LEAVE_TYPE_KEY,
        displayLabel,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', LEAVE_TYPE_MODULE, LEAVE_TYPE_KEY] });
      setNewLabel('');
      onLeaveTypeAdded?.(created.keyValue);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.deactivate(enumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', LEAVE_TYPE_MODULE, LEAVE_TYPE_KEY] });
      setConfirmItem(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.update(enumId, { isActive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', LEAVE_TYPE_MODULE, LEAVE_TYPE_KEY] });
    },
  });

  const activeTypes = leaveTypes.filter((item) => item.isActive);
  const inactiveTypes = leaveTypes.filter((item) => !item.isActive);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    createMutation.mutate(label);
  };

  const handleClose = () => {
    setNewLabel('');
    setConfirmItem(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Umbrella size={20} />
            Manage Leave Types
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Leave types are shared across all leave requests. Remove a type from the dropdown with the trash icon,
            or restore inactive ones below.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              label="New leave type name"
              placeholder="e.g. Maternity Leave"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleAdd}
              disabled={!newLabel.trim() || createMutation.isPending}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add
            </Button>
          </Box>

          {createMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(createMutation.error as Error).message || 'Failed to add leave type'}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert severity="error">Failed to load leave types.</Alert>
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Current Leave Types ({activeTypes.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: inactiveTypes.length > 0 ? 2 : 0 }}>
                {activeTypes.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.displayLabel}
                    onDelete={() => setConfirmItem(item)}
                    deleteIcon={<Trash2 size={14} />}
                  />
                ))}
              </Box>

              {inactiveTypes.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Inactive ({inactiveTypes.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {inactiveTypes.map((item) => (
                      <Chip
                        key={item.id}
                        label={item.displayLabel}
                        variant="outlined"
                        size="small"
                        onClick={() => reactivateMutation.mutate(item.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Click an inactive leave type to restore it.
                  </Typography>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmItem)} onClose={() => setConfirmItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Leave Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Deactivate &quot;{confirmItem?.displayLabel}&quot;? Existing leave requests keep their value,
            but it will no longer appear in the dropdown.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmItem && deactivateMutation.mutate(confirmItem.id)}
            disabled={deactivateMutation.isPending}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageLeaveTypesModal;
